//#region src/lib/oklch.ts
var toLinear = (c) => {
	const s = c / 255;
	return s <= .04045 ? s / 12.92 : ((s + .055) / 1.055) ** 2.4;
};
var fromLinear = (c) => 255 * (c <= .0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - .055);
/** Converts an opaque sRGB colour to OKLCH (Björn Ottosson's OKLab matrices). */
function rgbToOklch({ r, g, b }) {
	const [lr, lg, lb] = [
		toLinear(r),
		toLinear(g),
		toLinear(b)
	];
	const l_ = Math.cbrt(.4122214708 * lr + .5363325363 * lg + .0514459929 * lb);
	const m_ = Math.cbrt(.2119034982 * lr + .6806995451 * lg + .1073969566 * lb);
	const s_ = Math.cbrt(.0883024619 * lr + .2817188376 * lg + .6299787005 * lb);
	const L = .2104542553 * l_ + .793617785 * m_ - .0040720468 * s_;
	const A = 1.9779984951 * l_ - 2.428592205 * m_ + .4505937099 * s_;
	const B = .0259040371 * l_ + .7827717662 * m_ - .808675766 * s_;
	const c = Math.hypot(A, B);
	return {
		l: L,
		c,
		h: c < 1e-4 ? 0 : (Math.atan2(B, A) * 180 / Math.PI + 360) % 360
	};
}
/** Converts OKLCH to linear-light sRGB, which may be out of gamut. */
function oklchToLinear({ l, c, h }) {
	const A = c * Math.cos(h * Math.PI / 180);
	const B = c * Math.sin(h * Math.PI / 180);
	const l_ = (l + .3963377774 * A + .2158037573 * B) ** 3;
	const m_ = (l - .1055613458 * A - .0638541728 * B) ** 3;
	const s_ = (l - .0894841775 * A - 1.291485548 * B) ** 3;
	return [
		4.0767416621 * l_ - 3.3077115913 * m_ + .2309699292 * s_,
		-1.2684380046 * l_ + 2.6097574011 * m_ - .3413193965 * s_,
		-.0041960863 * l_ - .7034186147 * m_ + 1.707614701 * s_
	];
}
var inGamut = (channels) => channels.every((v) => v >= -1e-6 && v <= 1 + 1e-6);
/**
* Converts OKLCH to an opaque sRGB colour. Out-of-gamut colours keep their lightness
* and hue and lose chroma until they fit.
*/
function oklchToRgb(color) {
	const l = Math.min(1, Math.max(0, color.l));
	let channels = oklchToLinear({
		...color,
		l
	});
	if (!inGamut(channels)) {
		let [low, high] = [0, color.c];
		for (let i = 0; i < 24; i++) {
			const mid = (low + high) / 2;
			if (inGamut(oklchToLinear({
				l,
				c: mid,
				h: color.h
			}))) low = mid;
			else high = mid;
		}
		channels = oklchToLinear({
			l,
			c: low,
			h: color.h
		});
	}
	const [r, g, b] = channels.map((v) => Math.round(fromLinear(Math.min(1, Math.max(0, v)))));
	return {
		r: r ?? 0,
		g: g ?? 0,
		b: b ?? 0,
		a: 1
	};
}
/** Mixes two colours in OKLab; `amount` is the share of `a` (0–1). */
function mixOklab(a, b, amount) {
	const [x, y] = [rgbToOklch(a), rgbToOklch(b)];
	const toLab = ({ l, c, h }) => [
		l,
		c * Math.cos(h * Math.PI / 180),
		c * Math.sin(h * Math.PI / 180)
	];
	const [la, aa, ba] = toLab(x);
	const [lb, ab, bb] = toLab(y);
	const mix = (p, q) => p * amount + q * (1 - amount);
	const [L, A, B] = [
		mix(la, lb),
		mix(aa, ab),
		mix(ba, bb)
	];
	return oklchToRgb({
		l: L,
		c: Math.hypot(A, B),
		h: (Math.atan2(B, A) * 180 / Math.PI + 360) % 360
	});
}
/** Formats an opaque colour as #rrggbb. */
function toHex({ r, g, b }) {
	return `#${[
		r,
		g,
		b
	].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("")}`;
}
//#endregion
export { mixOklab, oklchToRgb, rgbToOklch, toHex };

//# sourceMappingURL=oklch.js.map